import { skipToken, useQuery } from '@tanstack/react-query';
import { getV1CompaniesCompanyIdOrdersOrderIdFileAttachments } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { FileAttachment } from '~hooks/useFileAttachment/models';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { Nullable } from '~types/Nullable';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

const EMPTY_ATTACHMENTS: FileAttachment[] = [];

// A cached row carries a signed file URL, so this has to stay well under the shortest
// service_urls_expire_in in any environment — 5 minutes, the Rails default, everywhere
// except production (1 day). Long enough that closing and reopening a preview is instant.
const STALE_TIME_IN_MS = 60 * 1000;

interface UseOrderFileAttachmentsQueryProps {
  enabled: boolean;
  orderId?: Nullable<string>;
}

// The orders list renders one card per order, so `enabled` keeps every card's query idle
// until a dispatcher actually asks for a preview.
export default function useOrderFileAttachmentsQuery({
  enabled,
  orderId,
}: UseOrderFileAttachmentsQueryProps) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const { data, isError, isFetching } = useQuery({
    queryKey: ['orderFileAttachments', companyId, orderId],
    queryFn:
      enabled && companyId && orderId
        ? async ({ signal }) => {
            try {
              const response = await getV1CompaniesCompanyIdOrdersOrderIdFileAttachments({
                path: { 'company-id': companyId, 'order-id': orderId },
                signal,
              });

              return response.data.data.map((item) => FileAttachment.parse(item));
            } catch (error) {
              connection.handleRequestError(
                error,
                t('error_messages.file_attachment.failed_to_fetch'),
              );
              throw error;
            }
          }
        : skipToken,
    // The queryFn toasts and reports through connection.handleRequestError, so React
    // Query's default of three retries would surface one failure four times.
    retry: false,
    staleTime: STALE_TIME_IN_MS,
  });

  return { attachments: data ?? EMPTY_ATTACHMENTS, isError, isFetching };
}
